<template>
  <CommonPages id="detail" class="detail" headerTheme="dark" :titlePrefix="formattedCarName"
    :metaDescription="description">
    <div class="detail__container">

      <!-- 顶部车辆信息和规格卡片区域 -->
      <div class="detail__top-section">
        <div class="detail__info-block">
          <div class="detail__info-background">
            <div class="detail__texture-overlay"></div>
          </div>

          <div class="detail__info-content">
            <!-- 顶部车辆信息 -->
            <div class="detail__header">
              <div class="detail__brand-logo-wrapper">
                <img :src="carData?.logo || ''" class="detail__brand-logo" alt="brand logo">
              </div>
              <div class="detail__header-info">
                <h1 class="detail__title">{{ carData?.model || 'Loading...' }}</h1>
                <div class="detail__brand-name">{{ carData?.brand || '' }}</div>
              </div>
            </div>

            <!-- 规格信息卡片 -->
            <div class="detail__info-cards">
              <div v-for="(card, index) in infoCards" :key="`info-card-${index}`"
                class="detail__info-card">
                <div class="detail__info-card-icon">
                  <img :src="card.icon" :alt="card.label">
                </div>
                <div class="detail__info-card-content">
                  <div class="detail__info-card-value">{{ card.value }}</div>
                  <div class="detail__info-card-label">{{ card.label }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 主图展示区域 -->
        <div class="detail__gallery-block">
          <div class="detail__gallery-main" v-if="carData?.gallery?.length > 0">
            <img :src="currentImage" alt="car image" class="detail__gallery-img">
          </div>

          <!-- 缩略图导航 -->
          <div class="detail__thumbnails-wrapper">
            <button class="detail__gallery-nav detail__gallery-prev">
              <svg xmlns="http://www.w3.org/2000/svg" width="6" height="12" viewBox="0 0 6 12"
                fill="none">
                <path
                  d="M1.81151 6L5.78094 1.46355C6.07302 1.12975 6.07302 0.584148 5.78094 0.250349C5.48887 -0.0834497 5.01146 -0.0834497 4.71938 0.250349L0.218581 5.39407C0.145855 5.47718 0.0907245 5.5737 0.0543615 5.67693C-0.00898042 5.8579 -0.0171914 6.05898 0.0309015 6.24532C0.0379395 6.27213 0.0461505 6.2976 0.0543615 6.32441C0.0907245 6.42764 0.145855 6.52416 0.218581 6.60727L4.71821 11.7497C5.01028 12.0835 5.48769 12.0835 5.77977 11.7497C6.07185 11.4159 6.07185 10.8702 5.77977 10.5364L1.81151 6Z"
                  fill="white" />
              </svg>
            </button>
            <div class="detail__thumbnails">
              <div v-for="(image, index) in carData?.gallery.slice(0, 5)" :key="`thumb-${index}`"
                class="detail__thumbnail-item"
                :class="{'detail__thumbnail-item--active': currentImageIndex === index}"
                @click="setCurrentImage(index)">
                <img :src="image" alt="car thumbnail" class="detail__thumbnail-img">
              </div>
            </div>
            <button class="detail__gallery-nav detail__gallery-next">
              <svg xmlns="http://www.w3.org/2000/svg" width="6" height="12" viewBox="0 0 6 12"
                fill="none">
                <path
                  d="M4.18849 6L0.219064 1.46355C-0.0730212 1.12975 -0.0730212 0.584148 0.219064 0.250349C0.511149 -0.0834497 0.988556 -0.0834497 1.28064 0.250349L5.78142 5.39407C5.85414 5.47718 5.90928 5.5737 5.94564 5.67693C6.00898 5.8579 6.01719 6.05898 5.9691 6.24532C5.96206 6.27213 5.95385 6.2976 5.94564 6.32441C5.90928 6.42764 5.85414 6.52416 5.78142 6.60727L1.28179 11.7497C0.989718 12.0835 0.51231 12.0835 0.220226 11.7497C-0.0718584 11.4159 -0.0718584 10.8702 0.220226 10.5364L4.18849 6Z"
                  fill="white" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- 移动端界面结构修改 -->
      <div class="detail__mobile-top" v-if="deviceType === 'mobile'">
        <div class="detail__mobile-background">
          <div class="detail__mobile-texture"></div>
        </div>

        <div class="detail__mobile-header">
          <div class="detail__mobile-brand">
            <div class="detail__mobile-logo-wrapper">
              <img :src="carData?.logo || ''" class="detail__mobile-logo" alt="brand logo">
            </div>
            <div class="detail__mobile-info">
              <h1 class="detail__mobile-title">{{ carData?.model || 'Loading...' }}</h1>
              <div class="detail__mobile-brand-name">{{ carData?.brand || '' }}</div>
            </div>
          </div>

          <div class="detail__mobile-cards">
            <div v-for="(card, index) in infoCards" :key="`mobile-card-${index}`"
              class="detail__mobile-card">
              <div class="detail__mobile-card-icon">
                <img :src="card.icon" :alt="card.label">
              </div>
              <div class="detail__mobile-card-content">
                <div class="detail__mobile-card-value">{{ card.value }}</div>
                <div class="detail__mobile-card-label">{{ card.label }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AdComponent v-if="deviceType === 'mobile'"
        v-show="$global.isObjectNotEmpty(adsensConfig) && adsensConfig?.detail_1" ref="ads-home-0"
        class="ad-width-100 ad-pos-row" data-ad-placement="detail_1" data-ad-source-placement="detail_1"
        :ads="adsensConfig?.detail_1" :showDebug="showDebug" />

      <!-- 移动端图片展示区域修改 -->
      <div class="detail__mobile-gallery"
        v-if="deviceType === 'mobile' && carData?.gallery?.length > 0">
        <div class="detail__mobile-gallery-main">
          <img :src="currentImage" alt="car image" class="detail__mobile-main-img">
        </div>
        <div class="detail__mobile-thumbnails">
          <div v-for="(image, index) in carData?.gallery.slice(0, 5)" :key="`mobile-thumb-${index}`"
            class="detail__mobile-thumbnail"
            :class="{'detail__mobile-thumbnail--active': currentImageIndex === index}"
            @click="setCurrentImage(index)">
            <img :src="image" alt="car thumbnail" class="detail__mobile-thumbnail-img">
          </div>
        </div>
      </div>

      <AdComponent v-if="deviceType !== 'mobile'"
        v-show="$global.isObjectNotEmpty(adsensConfig) && adsensConfig?.detail_1" ref="ads-home-0"
        class="pc_padding m_padding" data-ad-placement="detail_1" data-ad-source-placement="detail_1"
        :ads="adsensConfig?.detail_1" :showDebug="showDebug" />

      <!-- 主要内容区域 -->
      <div class="detail__content">
        <!-- 主要内容 -->
        <div class="detail__main">
          <!-- 详情内容区域 -->
          <div class="detail__sections">
            <div class="detail__sections-content">
              <template v-for="(section, index) in sections">
                <div :key="`section-content-${index}`" class="detail__section">
                  <div class="detail__section-header">
                    <h2 class="detail__section-title">{{ section.title }}</h2>
                  </div>
                  <div class="detail__section-content" v-html="section.content"></div>
                </div>

                <!-- 在sections中间位置插入相似价格车辆横向列表 -->
                <div v-if="deviceType === 'mobile' && index === Math.floor(sections.length / 2) - 1"
                  class="detail__mobile-similar-horizontal" :key="`similar-horizontal-${index}`">
                  <div class="detail__similar-horizontal-title">
                    <h2>Similar Priced Cars</h2>
                  </div>

                  <!-- 水平滚动列表 -->
                  <div class="detail__similar-horizontal-list">
                    <a v-for="(car, carIndex) in similarCars.slice(0, 4)"
                      :key="`similar-horizontal-${carIndex}`"
                      class="detail__similar-horizontal-item" :href="getCarDetailUrl(car)">

                      <div class="all-cars__car-header">
                        <div class="all-cars__brand-logo">
                          <img :src="car.logo" alt="brand logo">
                        </div>
                        <div class="all-cars__car-info">
                          <div class="all-cars__model">{{ car.model }}</div>
                          <div class="all-cars__brand">{{ car.brand }}</div>
                        </div>
                      </div>
                      <!-- 车辆图片 -->
                      <img :src="car.image" class="all-cars__pic" alt="car image">

                      <!-- 车辆信息 -->
                      <div class="all-cars__info">
                        <!-- 车辆类型 -->
                        <div class="all-cars__feature">
                          <img src="@/assets/calendar.svg" class="all-cars__icon" alt="year type">
                          <div class="all-cars__feature-text">{{ car.year }}</div>
                        </div>

                        <!-- 车辆类型 -->
                        <div class="all-cars__feature">
                          <img src="@/assets/car-type.svg" class="all-cars__icon" alt="car type">
                          <div class="all-cars__feature-text">{{ car.purpose || car.category }}
                          </div>
                        </div>

                        <!-- 车身类型 -->
                        <div class="all-cars__feature">
                          <img src="@/assets/body-type.svg" class="all-cars__icon" alt="body type">
                          <div class="all-cars__feature-text">{{ car.bodyType }}</div>
                        </div>

                        <div class="all-cars__feature">
                          <img src="@/assets/homehero/Budget.svg" class="all-cars__icon"
                            alt="Budget type">
                          <div class="all-cars__feature-text">{{ car.price }}</div>
                        </div>
                      </div>

                      <!-- 价格信息 -->
                      <div class="all-cars__price">
                        <div class="all-cars__price-head">Rate</div>
                        <div class="all-cars__price-text">{{ car.rate || '4.8' }} <span>/ 10</span>
                        </div>
                      </div>

                    </a>
                  </div>

                  <AdComponent v-if="deviceType === 'mobile'"
                    v-show="$global.isObjectNotEmpty(adsensConfig) && adsensConfig?.detail_2"
                    ref="ads-home-0" class="m_padding scrolling-section"
                    :ads="adsensConfig?.detail_2" :showDebug="showDebug" />

                </div>
              </template>
            </div>
          </div>

          <AdComponent v-if="deviceType === 'mobile'"
            v-show="$global.isObjectNotEmpty(adsensConfig) && adsensConfig?.detail_3"
            ref="ads-home-0" class="m_padding " :ads="adsensConfig?.detail_3"
            :showDebug="showDebug" />

          <!-- 移动端第一部分相似价格车辆 - 横向滚动样式 -->
          <div class="detail__mobile-similar-horizontal"
            v-if="deviceType === 'mobile' && sections.length <= 1">
            <div class="detail__similar-horizontal-title">
              <h2>Similar Priced Cars</h2>
            </div>

            <!-- 水平滚动列表 -->
            <div class="detail__similar-horizontal-list">
              <a v-for="(car, carIndex) in similarCars.slice(0, 4)"
                :key="`similar-horizontal-${carIndex}`" class="detail__similar-horizontal-item"
                :href="getCarDetailUrl(car)">

                <div class="all-cars__car-header">
                  <div class="all-cars__brand-logo">
                    <img :src="car.logo" alt="brand logo">
                  </div>
                  <div class="all-cars__car-info">
                    <div class="all-cars__model">{{ car.model }}</div>
                    <div class="all-cars__brand">{{ car.brand }}</div>
                  </div>
                </div>
                <!-- 车辆图片 -->
                <img :src="car.image" class="all-cars__pic" alt="car image">

                <!-- 车辆信息 -->
                <div class="all-cars__info">
                  <!-- 车辆类型 -->
                  <div class="all-cars__feature">
                    <img src="@/assets/calendar.svg" class="all-cars__icon" alt="year type">
                    <div class="all-cars__feature-text">{{ car.year }}</div>
                  </div>

                  <!-- 车辆类型 -->
                  <div class="all-cars__feature">
                    <img src="@/assets/car-type.svg" class="all-cars__icon" alt="car type">
                    <div class="all-cars__feature-text">{{ car.purpose || car.category }}</div>
                  </div>

                  <!-- 车身类型 -->
                  <div class="all-cars__feature">
                    <img src="@/assets/body-type.svg" class="all-cars__icon" alt="body type">
                    <div class="all-cars__feature-text">{{ car.bodyType }}</div>
                  </div>

                  <div class="all-cars__feature">
                    <img src="@/assets/homehero/Budget.svg" class="all-cars__icon"
                      alt="Budget type">
                    <div class="all-cars__feature-text">{{ car.price }}</div>
                  </div>
                </div>

                <!-- 价格信息 -->
                <div class="all-cars__price">
                  <div class="all-cars__price-head">Rate</div>
                  <div class="all-cars__price-text">{{ car.rate || '4.8' }} <span>/ 10</span></div>
                </div>

              </a>
            </div>
          </div>

          <!-- 移动端第二部分相似价格车辆 - 垂直列表样式 -->
          <div class="detail__mobile-similar-vertical" v-if="deviceType === 'mobile'">
            <div class="detail__similar-vertical-title">
              <h2>Similar Priced Cars</h2>
            </div>

            <!-- 将 SimilarPricedCars 组件替换回原来的垂直列表结构 -->
            <div class="detail__similar-vertical-list">
              <a v-for="(car, index) in similarCars.slice(4, 8)" :key="`similar-vertical-${index}`"
                class="detail__similar-vertical-item" :href="getCarDetailUrl(car)">

                <div class="all-cars__car-header">
                  <div class="all-cars__brand-logo">
                    <img :src="car.logo" alt="brand logo">
                  </div>
                  <div class="all-cars__car-info">
                    <div class="all-cars__model">{{ car.model }}</div>
                    <div class="all-cars__brand">{{ car.brand }}</div>
                  </div>
                </div>
                <!-- 车辆图片 -->
                <img :src="car.image" class="all-cars__pic" alt="car image">

                <!-- 车辆信息 -->
                <div class="all-cars__info">
                  <!-- 车辆类型 -->
                  <div class="all-cars__feature">
                    <img src="@/assets/calendar.svg" class="all-cars__icon" alt="year type">
                    <div class="all-cars__feature-text">{{ car.year }}</div>
                  </div>

                  <!-- 车辆类型 -->
                  <div class="all-cars__feature">
                    <img src="@/assets/car-type.svg" class="all-cars__icon" alt="car type">
                    <div class="all-cars__feature-text">{{ car.purpose || car.category }}</div>
                  </div>

                  <!-- 车身类型 -->
                  <div class="all-cars__feature">
                    <img src="@/assets/body-type.svg" class="all-cars__icon" alt="body type">
                    <div class="all-cars__feature-text">{{ car.bodyType }}</div>
                  </div>

                  <div class="all-cars__feature">
                    <img src="@/assets/homehero/Budget.svg" class="all-cars__icon"
                      alt="Budget type">
                    <div class="all-cars__feature-text">{{ car.price }}</div>
                  </div>
                </div>

                <!-- 价格信息 -->
                <div class="all-cars__price">
                  <div class="all-cars__price-head">Rate</div>
                  <div class="all-cars__price-text">{{ car.rate || '4.8' }} <span>/ 10</span></div>
                </div>

              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 相似价格车辆推荐模块 (仅在PC端显示) -->
    <SimilarPricedCars v-if="deviceType !== 'mobile' && carData && carData.brand"
      :carList="similarCars" :getCarDetailUrl="getCarDetailUrl" />

  </CommonPages>
</template>

<script>
import common from '@/mixin/common.js'
import { mapGetters } from 'vuex';
import CommonPages from '@/pages/components/page_common.vue'
import SimilarPricedCars from '@/components/SimilarPricedCars.vue'
import AdComponent from '@/pages/components/AdComponent.vue'
import adMixin from '@/mixin/adMixin.js'
import adTrackingMixin from '@/mixin/adTrackingMixin.js'
import reportMixin from '@/mixin/reportMixin.js'

import '@/css/home.scss';


export default {
  name: 'Detail',
  components: {
    CommonPages,
    SimilarPricedCars,
    AdComponent
  },
  mixins: [common, adMixin, adTrackingMixin, reportMixin],
  props: {
    carName: {
      type: String,
      default: ''
    },
    id: {
      type: String,
      default: ''
    }
  },
  computed: {
    ...mapGetters({
      deviceType: 'deviceType',
      adsensConfig: 'adsensConfig',
      adPosConfig: 'adPosConfig',
      showDebug: 'showDebug'
    }),
    formattedCarName () {
      // 将URL中的连字符替换回空格并进行格式化
      return this.carName ? decodeURIComponent(this.carName).replace(/-/g, ' ') : '';
    },
    description () {
      return `${this.domainConfig?.['meta']?.des || 'Default des'}`
    },
    currentImage () {
      if (!this.carData || !this.carData.gallery || !Array.isArray(this.carData.gallery) || this.carData.gallery.length === 0) {
        return '';
      }
      const index = Math.min(this.currentImageIndex, this.carData.gallery.length - 1);
      return this.carData.gallery[index] || '';
    },
    galleryLength () {
      return this.carData && this.carData.gallery ? this.carData.gallery.length : 0;
    },
    infoCards () {
      // 生成车辆信息卡片数据
      return [
        {
          icon: require('@/assets/detail-icons/traffic_jam.svg'),
          value: this.carData?.category || 'Sedan',
          label: 'Type'
        },
        {
          icon: require('@/assets/detail-icons/calendar_month.svg'),
          value: this.carData?.year || 'Jan 2025',
          label: 'Ex-Showroom Time'
        },
        {
          icon: require('@/assets/detail-icons/paid.svg'),
          value: this.carData?.price || '$50,001-$100,000',
          label: 'Ex-Showroom Price'
        },
        {
          icon: require('@/assets/detail-icons/local_gas_station.svg'),
          value: this.carData?.fuel || 'Gasoline',
          label: 'Fuel'
        },
        {
          icon: require('@/assets/detail-icons/crossword.svg'),
          value: this.carData?.purpose || 'Commercial Vehicle',
          label: 'Purpose'
        },
        {
          icon: require('@/assets/detail-icons/award_star.svg'),
          value: this.carData?.rate || '9.5',
          label: 'Rate'
        },
        {
          icon: require('@/assets/detail-icons/readiness_score.svg'),
          value: this.carData?.speedValue || '9.6s',
          label: '0-60-MPH Times'
        }
      ];
    }
  },
  data () {
    return {
      currentImageIndex: 0,
      activeSection: 0,
      loading: false,
      error: null,
      carData: {
        gallery: [], // 初始化空数组，避免在数据加载前引用报错
        model: '',
        brand: '',
        logo: '',
        year: '',
        category: '',
        bodyType: '',
        fuel: '',
        price: '',
        rate: '',
        speedValue: ''
      },
      similarCars: [],
      sections: [],
      isLoading: true,
      scrollTop: 0
    }
  },
  beforeMount () {
    // 保存上一个页面的路径和参数到Vuex
    this.saveNavigationState();
  },
  async mounted () {
    // 获取车辆详情数据
    this.fetchCarInfo();

    // await this.initAdSystem();
    // // 确保在广告系统初始化后再初始化跟踪
    // this.$nextTick(() => {
    //   this.initAdTracking();
    // });

    // 监听滚动事件以更新活动章节
    window.addEventListener('scroll', this.handleScroll);
  },
  methods: {
    // 保存导航状态到vuex
    saveNavigationState () {
      // 获取前一个页面路径
      const previousPage = document.referrer;

      // 检查是否从all.vue页面来的
      const isFromAll = previousPage &&
        (previousPage.includes('/car-list') || previousPage.includes('/all'));

      if (isFromAll) {
        // 如果是从列表页来的，记录状态
        this.$store.commit('SET_FROM_DETAIL', true);
        this.$store.commit('SET_LAST_VISITED_PAGE', previousPage);

        // 保存查询参数
        const queryParams = { ...this.$route.query };
        this.$store.commit('SET_QUERY_PARAMS', queryParams);
      }
    },

    // 获取车辆详情
    async fetchCarInfo () {
      if (!this.id) {
        console.error('[Detail] Missing car ID');
        return;
      }

      try {
        this.loading = true;
        this.error = null;
        const themeFilters = this.$store.state.domainConfig?.themeFilters || {};
        // 获取webIndex
        const webIndex = this.$store.state.domainConfig?.webIndex || 1;

        console.log(themeFilters);
        const res = await this.$global.server.getCarInfo({
          id: this.id,
          ...themeFilters,
          webIndex
        });

        if (res.code === 200 && res.data) {
          console.log('[Detail] Car detail data fetched successfully:', res.data);

          // 从接口返回的carInfo获取车辆详情
          const carInfo = res.data.carInfo || res.data;

          // Extract gallery images
          const photos = carInfo.photos ? carInfo.photos.split(',') : [];
          const gallery = photos.length > 0 ?
            photos : [require('@/assets/car-recommend-img.png')];

          // 创建新的carData对象，保留默认值结构
          this.carData = {
            gallery: gallery, // 确保gallery总是一个数组
            id: carInfo.id || '',
            model: carInfo.name || 'Unknown Model',
            brand: carInfo.brand || 'Unknown Brand',
            logo: carInfo.logoUrl || require('@/assets/car-logo.svg'), // 优先使用接口返回的logo，没有则使用默认logo
            year: carInfo.marketTime || carInfo.year || '-',
            category: carInfo.purpose || '-',
            bodyType: carInfo.bodyType || carInfo.type || '-',
            purpose: carInfo.purpose || '-',
            fuel: carInfo.fuel || '-',
            price: this.formatPrice(carInfo.leastPrice, carInfo.mostPrice) || '-',
            rate: carInfo.rate || '0',
            speedValue: carInfo.accelerationTime ? `${carInfo.accelerationTime}s` : '-'
          };

          // 初始化sections为空数组
          this.sections = [];

          // 将API返回的各字段转换为sections数组
          const sectionFields = [
            { field: 'overall', title: 'Overview' },
            { field: 'endurance', title: 'Endurance' },
            { field: 'safety', title: 'Safety' },
            { field: 'fuelEconomy', title: 'Fuel Economy' },
            { field: 'interiorAndComfort', title: 'Interior and Comfort' },
            { field: 'infotainmentAndConnectivity', title: 'Infotainment and Connectivity' },
            { field: 'warrantyAndMaintenanceCoverage', title: 'Warranty and Maintenance Coverage' }
          ];

          // 将每个字段转换为section对象
          sectionFields.forEach(section => {
            if (carInfo[section.field] && carInfo[section.field].trim()) {
              this.sections.push({
                title: section.title,
                content: carInfo[section.field]
              });
            }
          });

          // 重置图片显示索引
          this.currentImageIndex = 0;

          // 从接口返回的recommendList获取相似车辆
          if (res.data.recommendList && Array.isArray(res.data.recommendList)) {
            // 处理API返回的数据
            this.similarCars = res.data.recommendList.map(item => this.formatCarData(item));
            console.log(`[Detail] 获取到相似车辆: ${this.similarCars.length}`);
          } else {
            const type = carInfo.type || '';
            const purpose = carInfo.purpose || '';
            const fuel = carInfo.fuel || '';

            console.log(`[Detail] 提取关键字段重新请求推荐: type=${type}, purpose=${purpose}, fuel=${fuel}`);

            // 使用提取的字段再次请求获取推荐列表
            await this.fetchRecommendList(type, purpose, fuel);
          }

        } else {
          throw new Error(res.message || 'Failed to fetch car details');
        }
      } catch (error) {
        console.error('[Detail] Error fetching car details:', error);
        this.error = error.message || 'Failed to fetch car details';

        // 确保carData保持一个有效的结构，即使请求失败
        this.carData = {
          gallery: [require('@/assets/car-recommend-img.png')],
          model: 'Unknown Model',
          brand: 'Unknown Brand',
          logo: require('@/assets/car-logo.svg'),
          year: '-',
          category: '-',
          bodyType: '-',
          purpose: '-',
          fuel: '-',
          price: '-',
          rate: '0',
          speedValue: '-'
        };
      } finally {
        this.loading = false;
      }
    },

    // 使用关键字段请求推荐列表
    async fetchRecommendList (type, purpose, fuel) {
      try {
        console.log('[Detail] 请求相关推荐列表');

        // 获取webIndex
        const webIndex = this.$store.getters.domainConfig?.webIndex || 1;

        // 构建请求参数
        const params = {
          id: this.id,
          type: type,
          purpose: purpose,
          fuel: fuel,
          webIndex
        };

        // 使用修改后的参数再次请求getCarInfo接口
        const response = await this.$global.server.getCarInfo(params);

        if (response && response.code === 200 && response.data) {
          // 检查返回的数据中是否包含recommendList
          if (response.data.recommendList && Array.isArray(response.data.recommendList)) {
            this.similarCars = response.data.recommendList.map(item => this.formatCarData(item));
            console.log(`[Detail] 根据关键字段获取到相似车辆: ${this.similarCars.length}`);
          } else {
            console.log('[Detail] 第二次请求未返回推荐列表，尝试获取相似车辆');
            this.fetchSimilarCars();
          }
        } else {
          console.warn('[Detail] 第二次请求获取推荐失败，尝试获取相似车辆');
          this.fetchSimilarCars();
        }
      } catch (error) {
        console.error('[Detail] 获取推荐列表出错:', error);
        // 如果获取推荐列表失败，尝试获取相似车辆
        this.fetchSimilarCars();
      }
    },

    // 格式化价格显示
    formatPrice (leastPrice, mostPrice) {
      if (!leastPrice && !mostPrice) return null;

      const formatNumber = (num) => {
        if (!num) return null;
        // 将价格格式化为$xx,xxx格式
        return '$' + num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      };

      if (leastPrice && mostPrice) {
        // 有最低价和最高价，显示价格区间
        return `${formatNumber(leastPrice)}-${formatNumber(mostPrice)}`;
      } else if (leastPrice) {
        // 只有最低价
        return `>${formatNumber(leastPrice)}`;
      } else if (mostPrice) {
        // 只有最高价
        return `<${formatNumber(mostPrice)}`;
      }

      return null;
    },

    setCurrentImage (index) {
      this.currentImageIndex = index;
    },
    handleScroll () {
      // 基于滚动位置更新活动章节的逻辑
      const sections = document.querySelectorAll('.detail__section');
      const scrollPosition = window.scrollY + window.innerHeight / 3;

      sections.forEach((section, index) => {
        const top = section.offsetTop;
        const bottom = top + section.offsetHeight;

        if (scrollPosition >= top && scrollPosition < bottom) {
          this.activeSection = index;
        }
      });
    },

    // 跳转到全部车辆页面
    gotoAll () {
      window.location.href = this.getAllCarsUrl();
    },

    // 跳转到车辆详情页面
    gotoDetail (car) {
      window.location.href = this.getCarDetailUrl(car);
    },

    // 格式化车辆数据
    formatCarData (item) {
      // 格式化车辆数据以便在页面上显示
      const formattedItem = { ...item };
      formattedItem.logo = item.logo || require('@/assets/car-logo.svg');

      // // 处理图片
      // if (item.photos && item.photos.length > 0) {
      //   // 如果photos是字符串（逗号分隔的URL列表），则转换为数组
      //   if (typeof item.photos === 'string') {
      //     const photoArray = item.photos.split(',');
      //     formattedItem.image = photoArray[0]; // 使用第一张图片作为主图
      //   } else {
      //     formattedItem.image = item.photos[0];
      //   }
      // } else {
      //   // 使用默认图片
      //   formattedItem.image = require('@/assets/car-recommend-img.png');
      // }

      formattedItem.image = item.cover;

      // 处理模型名称
      if (!formattedItem.model && item.name) {
        formattedItem.model = item.name;
      }

      // 确保category和bodyType有值
      formattedItem.category = item.purpose || item.type || 'N/A';
      formattedItem.bodyType = item.type || formattedItem.category || 'N/A';

      // 处理年份
      formattedItem.year = item.year || item.marketTime?.substring(0, 4) || '-';

      // 处理价格
      formattedItem.price = this.formatPrice(item.leastPrice, item.mostPrice) || item.price || '-';

      // 处理加速时间
      formattedItem.speedValue = item.accelerationTime ? `${item.accelerationTime}s` : '-';

      // 处理评分
      formattedItem.rate = item.rate || '4.5';

      return formattedItem;
    },

    // 获取相似车辆
    async fetchSimilarCars () {
      try {
        // 构建API参数
        const params = {
          pageSize: 8,
          pageNum: 1,
          total: 30, // 添加total参数，默认值为30
          brand: this.carData.brand || ''
        };

        // 从store获取域名配置的主题过滤条件
        const themeFilters = this.$store.getters.domainConfig?.themeFilters || {};

        // 合并主题过滤参数
        const requestParams = { ...params, ...themeFilters };

        console.log(`[Detail] 获取相似车辆: brand=${params.brand}, total=30, themeFilters=${JSON.stringify(themeFilters)}`);

        // 调用searchCar接口
        const response = await this.$global.server.searchCar(requestParams);

        if (response && response.code === 200 && response.data && response.data.list) {
          // 处理API返回的数据
          this.similarCars = response.data.list.map(this.formatCarData);
          console.log(`[Detail] 获取到相似车辆: ${this.similarCars.length}`);
        } else {
          console.warn('[Detail] 获取相似车辆失败:', response?.message || '未知错误');
          this.similarCars = [];
        }
      } catch (error) {
        console.error('[Detail] 获取相似车辆数据出错:', error);
        this.similarCars = [];
      }
    },

    // 生成车辆详情页面的URL
    getCarDetailUrl (car) {
      if (!car || !car.id) {
        return '#';
      }
      const currentChannel = this.$router.currentRoute.params.channel;
      const formattedName = encodeURIComponent((car.model || car.name || '').replace(/\s+/g, '-'));
      const currentQuery = { ...this.$route.query };
      const queryString = Object.keys(currentQuery).length > 0
        ? `?${new URLSearchParams(currentQuery).toString()}`
        : '';

      if (currentChannel) {
        const baseUrl = window.location.origin;
        return `${baseUrl}/${currentChannel}/cars/${formattedName}/${car.id}${queryString}`;
      } else {
        return `/cars/${formattedName}/${car.id}${queryString}`;
      }
    },
    getAllCarsUrl () {
      const currentChannel = this.$router.currentRoute.params.channel;
      const currentQuery = { ...this.$route.query };
      const queryString = Object.keys(currentQuery).length > 0
        ? `?${new URLSearchParams(currentQuery).toString()}`
        : '';
      return currentChannel ? `/${currentChannel}/car-list${queryString}` : `/car-list${queryString}`;
    }
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this.handleScroll);
  }
}
</script>

<style lang="scss" scoped>
@import '~@/css/variables.scss';

.detail {
	background: #fff;
	min-height: 100vh;

	&__container {
		max-width: 100%;
		margin: 0 auto;
	}

	&__top-section {
		display: flex;
		flex-direction: row;
		width: 100%;
	}

	&__info-block {
		position: relative;
		width: rem(692);
		height: rem(582);
		flex-shrink: 0;
	}

	&__info-background {
		background: #141414;
		border-radius: rem(0) rem(0) rem(36) rem(0);
		position: absolute;
		width: 100%;
		height: 100%;
		left: 0;
		top: 0;
	}

	&__texture-overlay {
		position: absolute;
		width: 100%;
		height: 100%;
		left: 0;
		top: 0;
		background-image: url('~@/assets/detail-texture.png');
		background-size: cover;
		border-radius: rem(0) rem(0) rem(36) rem(0);
	}

	&__info-content {
		position: relative;
		z-index: 1;
		padding: rem(80) rem(120) rem(80) rem(120);
		display: flex;
		flex-direction: column;
		gap: rem(60);
	}

	&__header {
		display: flex;
		flex-direction: row;
		gap: rem(16);
		align-items: center;
	}

	&__brand-logo-wrapper {
		width: rem(58);
		height: rem(58);
		border-radius: 50%;
		background: #ffffff;
		border: 1px solid #e9e9e9;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	&__brand-logo {
		width: rem(48);
		height: rem(48);
		object-fit: contain;
		border-radius: 50%;
	}

	&__header-info {
		display: flex;
		flex-direction: column;
		gap: rem(12);
	}

	&__title {
		font-size: rem(32);
		line-height: rem(32);
		color: #ffffff;
		margin: 0;
		font-weight: 700;
	}

	&__brand-name {
		font-size: rem(14);
		line-height: rem(14);
		color: #ffffff;
		font-weight: 400;
	}

	/* 卡片信息区域 */
	&__info-cards {
		display: flex;
		flex-wrap: wrap;
		gap: rem(16);
	}

	&__info-card {
		background: #ffffff;
		border: 1px solid #e9e9e9;
		border-radius: rem(12);
		padding: rem(16);
		display: flex;
		align-items: center;
		gap: rem(12);
		width: calc(50% - rem(8));
		transition: transform 0.3s ease;

		&:hover {
			transform: translateY(-4px);
		}
	}

	&__info-card-icon {
		width: rem(32);
		height: rem(32);
		display: flex;
		align-items: center;
		justify-content: center;
		background: var(--theme-color);
		border-radius: rem(4);

		img {
			width: rem(16);
			height: rem(16);
		}
	}

	&__info-card-content {
		display: flex;
		flex-direction: column;
		gap: rem(6);
	}

	&__info-card-value {
		font-size: rem(14);
		line-height: rem(14);
		font-weight: 500;
		color: #141414;
	}

	&__info-card-label {
		font-size: rem(12);
		line-height: rem(12);
		color: #777777;
	}

	/* 画廊区域 */
	&__gallery-block {
		flex: 1;
		display: flex;
		flex-direction: column;
		gap: rem(20);
	}

	&__gallery-main {
		width: 100%;
		height: rem(496);
	}

	&__gallery-img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		border-radius: rem(0) rem(0) rem(36) rem(0);
	}

	&__thumbnails-wrapper {
		display: flex;
		align-items: center;
		gap: rem(20);
		padding: 0 rem(30);
	}

	&__gallery-nav {
		width: rem(24);
		height: rem(24);
		background: #141414;
		border-radius: rem(8);
		border: none;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		flex-shrink: 0;

		&:hover {
			background: #333;
		}
	}

	&__thumbnails {
		display: flex;
		gap: rem(12);
		overflow-x: auto;
		flex: 1;
		padding-top: rem(5);

		&::-webkit-scrollbar {
			display: none;
		}

		scrollbar-width: none;
	}

	&__thumbnail-item {
		width: rem(100);
		height: rem(66);
		border-radius: rem(12);
		overflow: hidden;
		border: 1px solid #e9e9e9;
		cursor: pointer;
		transition: all 0.3s ease;

		&--active {
			border: 2px solid var(--theme-color);
		}

		&:hover {
			transform: translateY(-4px);
		}
	}

	&__thumbnail-img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	/* 内容区域 */
	&__content {
		max-width: rem(1200);
		margin: 0 auto;
		padding: rem(40) rem(20);
	}

	&__section {
		margin-bottom: rem(40);

		&:last-child {
			margin-bottom: 0;
			border-bottom: none;
		}
	}

	&__section-header {
		display: flex;
		align-items: center;
		margin-bottom: rem(16);
		position: relative;
	}

	&__section-title {
		color: #141414;
		font-size: rem(24);
		font-weight: 700;
		line-height: rem(28);
		margin: 0;
	}

	&__section-content {
		color: #555555;
		font-size: rem(16);
		font-weight: 400;
		line-height: rem(26);

		p {
			margin-bottom: rem(16);
		}

		ul {
			padding-left: rem(20);
			margin-bottom: rem(16);

			li {
				margin-bottom: rem(8);
			}
		}
	}

	// 移动端相似车辆垂直列表样式
	&__mobile-similar-horizontal {
		margin-bottom: rem(30);
		padding-left: rem(16);
	}

	&__similar-horizontal-title {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: rem(20);

		h2 {
			color: #141414;
			font-size: rem(24);
			font-style: normal;
			font-weight: 700;
		}
	}

	&__similar-horizontal-list {
		display: flex;
		flex-direction: row;
		gap: rem(16);
		overflow-x: auto;
		scrollbar-width: none; /* Firefox */
		-ms-overflow-style: none; /* IE and Edge */

		&::-webkit-scrollbar {
			display: none; /* Chrome, Safari, Opera */
		}
	}

	&__similar-horizontal-item {
		flex: 0 0 rem(286);
		overflow: hidden;
		background: linear-gradient(180deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.08) 50%, rgba(var(--theme-color-r), var(--theme-color-g), var(--theme-color-b), 0.08) 100%);
	}

	&__similar-horizontal-image {
		position: relative;

		img {
			width: 100%;
			height: rem(180);
			object-fit: cover;
			display: block;
		}
	}

	&__similar-horizontal-speed {
		position: absolute;
		top: rem(8);
		left: rem(8);
		background: rgba(0, 0, 0, 0.5);
		border-radius: rem(6);
		color: white;
		padding: rem(4) rem(8);
		font-size: rem(12);
		font-weight: bold;
		display: flex;
		align-items: center;

		span {
			margin-left: rem(6);
			font-size: rem(10);
			font-weight: normal;
		}
	}

	&__similar-horizontal-info {
		padding: rem(16);
	}

	&__similar-horizontal-header {
		display: flex;
		justify-content: space-between;
		align-items: flex-start;
		margin-bottom: rem(10);
	}

	&__similar-horizontal-model {
		font-size: rem(16);
		color: #050b20;
		margin-bottom: rem(4);
	}

	&__similar-horizontal-brand {
		font-size: rem(12);
		color: #959eac;
	}

	&__similar-horizontal-logo {
		width: rem(32);
		height: rem(32);
	}

	&__similar-horizontal-rating {
		display: flex;
		align-items: center;
		gap: rem(4);
		margin-bottom: rem(10);
		color: #4c5164;
		font-size: rem(12);

		img {
			width: rem(14);
			height: rem(14);
		}
	}

	&__similar-horizontal-rate {
		color: #405ff2;
		font-weight: bold;
	}

	&__similar-horizontal-price {
		display: flex;
		justify-content: space-between;
		align-items: center;
	}

	&__similar-horizontal-price-text {
		font-size: rem(14);
		color: #050b20;
	}

	&__similar-horizontal-price-link {
		width: rem(16);
		height: rem(16);
		display: flex;
		align-items: center;
		justify-content: center;

		img {
			width: 100%;
			height: 100%;
		}
	}

	&__mobile-similar-vertical {
		margin-top: rem(40);
	}

	&__similar-vertical-title {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: rem(20);
		padding: 0 rem(16);

		h2 {
			font-size: rem(24);
			font-style: normal;
			font-weight: 700;
			line-height: rem(24); /* 100% */
			color: #141414;
			margin: 0;
		}
	}

	&__view-all {
		display: flex;
		align-items: center;
		gap: rem(6);
		color: #405ff2;

		font-size: rem(14);

		img {
			width: rem(14);
			height: rem(14);
		}
	}

	&__similar-vertical-list {
		display: flex;
		flex-direction: column;
		gap: rem(16);
		padding: 0 rem(16);
	}

	&__similar-vertical-item {
		border-radius: rem(12);
		overflow: hidden;
		background: #ffffff;
		border: 1px solid #e9e9e9;
		cursor: pointer;
		position: relative;
	}

	&__similar-vertical-pic {
		width: 100%;
		height: rem(185);
		object-fit: cover;
		z-index: 1;
	}

	&__similar-vertical-speed {
		position: absolute;
		top: rem(6);
		left: rem(6);
		z-index: 2;
		display: flex;
		background: rgba(0, 0, 0, 0.5);
		border-radius: rem(8);
		overflow: hidden;
	}

	&__similar-vertical-speed-value {
		background: rgba(0, 0, 0, 0.5);
		padding: rem(4) rem(8);
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: center;
	}

	&__similar-vertical-speed-number {
		color: #ffffff;
		text-align: center;
		font-size: rem(12);
		line-height: rem(12);
		font-weight: 700;
	}

	&__similar-vertical-speed-label {
		padding: rem(4) rem(8);
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: center;
	}

	&__similar-vertical-speed-text {
		color: #ffffff;
		text-align: center;
		font-size: rem(10);
		line-height: rem(10);
		font-weight: 400;
	}

	&__similar-vertical-baseinfo {
		background: rgba(255, 255, 255, 0.5);
		backdrop-filter: blur(10px);
		border-radius: rem(12);
		padding: rem(16);
		display: flex;
		flex-direction: column;
		gap: rem(10);
		align-items: flex-start;
		justify-content: flex-start;
		width: 100%;
		z-index: 2;
	}

	&__similar-vertical-info {
		display: flex;
		flex-direction: column;
		gap: rem(16);
		align-items: flex-start;
		justify-content: flex-start;
		align-self: stretch;
		flex-shrink: 0;
		position: relative;
	}

	&__similar-vertical-card-title {
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: space-between;
		align-self: stretch;
		flex-shrink: 0;
		position: relative;
	}

	&__similar-vertical-name {
		display: flex;
		flex-direction: column;
		gap: rem(8);
		align-items: flex-start;
		justify-content: flex-start;
		position: relative;
	}

	&__similar-vertical-model {
		color: #050b20;
		text-align: left;
		font-size: rem(16);
		line-height: rem(16);
		font-weight: 700;
		position: relative;
		display: flex;
		align-items: center;
		justify-content: flex-start;
		overflow: hidden;
		display: -webkit-box;
		text-overflow: ellipsis;
		-webkit-line-clamp: 1;
		-webkit-box-orient: vertical;
	}

	&__similar-vertical-brand {
		color: #848998;
		text-align: left;
		font-size: rem(12);
		line-height: rem(12);
		font-weight: 400;
		position: relative;
		display: flex;
		align-items: center;
		justify-content: flex-start;
	}

	&__similar-vertical-logo {
		flex-shrink: 0;
		width: rem(36);
		height: rem(36);
		background: #fff;
		border-radius: 50%;
		padding: rem(2);
		object-fit: contain;
		position: relative;
	}

	&__similar-vertical-divider {
		background: #050b20;
		opacity: 0.1;
		align-self: stretch;
		flex-shrink: 0;
		height: rem(1);
		position: relative;
	}

	&__similar-vertical-type {
		display: flex;
		flex-direction: column;
		gap: rem(8);
		align-items: flex-start;
		justify-content: flex-start;
		align-self: stretch;
		flex-shrink: 0;
		position: relative;
	}

	&__similar-vertical-type-item {
		display: flex;
		flex-direction: row;
		gap: rem(9);
		align-items: center;
		justify-content: flex-start;
		flex-shrink: 0;
		position: relative;
	}

	&__similar-vertical-type-icon {
		flex-shrink: 0;
		width: rem(12);
		height: rem(12);
		position: relative;
	}

	&__similar-vertical-type-text {
		color: #050b20;
		text-align: left;
		font-size: rem(12);
		line-height: rem(12);
		font-weight: 400;
		position: relative;
		display: flex;
		align-items: center;
		justify-content: flex-start;
	}

	&__similar-vertical-price {
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: space-between;
		align-self: stretch;
		flex-shrink: 0;
		position: relative;
	}

	&__similar-vertical-price-text {
		color: #050b20;
		text-align: left;
		font-size: rem(18);
		line-height: rem(18);
		font-weight: 700;
		position: relative;
		display: flex;
		align-items: center;
		justify-content: flex-start;
	}

	&__similar-vertical-price-link {
		border-radius: rem(50%);
		width: rem(16);
		height: rem(16);
		position: relative;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
	}

	&__similar-vertical-arrow {
		flex-shrink: 0;
		width: rem(16);
		height: rem(16);
		position: relative;
		overflow: visible;
	}

	&__box-btn {
		background: var(--theme-color);
	}

	&__spec-item {
		border: 2px solid var(--theme-color);
	}

	&__card {
		background: linear-gradient(180deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.08) 50%, rgba(var(--theme-color-r), var(--theme-color-g), var(--theme-color-b), 0.08) 100%);
	}

	&__overview-time {
		color: var(--theme-color);
	}

	&__info-box {
		background: linear-gradient(180deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.08) 50%, rgba(var(--theme-color-r), var(--theme-color-g), var(--theme-color-b), 0.08) 100%);
	}

	&__compare-btn {
		background: var(--theme-color);
	}

	&__contact-btn {
		border: 3px solid var(--theme-color);
	}
}

/* 为detail.vue中的卡片添加all.vue的样式 */
.all-cars {
	&__car-header {
		display: flex;
		align-items: center;
		gap: rem(20);
		margin-bottom: rem(20);
	}

	&__brand-logo {
		width: rem(40);
		height: rem(40);
		display: flex;
		align-items: center;
		justify-content: center;
		background: #ffffff;
		border-radius: 50%;
		border-radius: rem(100);
		border: 1px solid #e9e9e9;
		background: lightgray 50% / cover no-repeat, #fff;

		img {
			width: rem(40);
			height: rem(40);
			object-fit: contain;
		}
	}

	&__car-info {
		display: flex;
		flex-direction: column;
		gap: rem(8);
	}

	&__model {
		font-size: rem(16);
		font-weight: 600;
		line-height: rem(16); /* 100% */
		color: #141414;
	}

	&__brand {
		font-size: rem(12);
		line-height: rem(12); /* 100% */
		color: #777777;
	}

	&__pic {
		width: 100%;
		height: rem(164);
		object-fit: cover;
		border-radius: rem(16);
		border: rem(1) solid #fff;
		background: lightgray 50% / cover no-repeat;
	}

	&__info {
		padding: rem(12) 0;
		display: flex;
		flex-direction: column;
		gap: rem(8);
	}

	&__feature {
		display: flex;
		align-items: center;
		gap: rem(10);
	}

	&__icon {
		width: rem(16);
		height: rem(16);
	}

	&__feature-text {
		font-size: rem(14);
		color: #141414;
	}

	&__price {
		padding: rem(16) 0 0;
		border-top: 2px solid #fff;
		display: flex;
		align-items: center;
		justify-content: flex-start;
		gap: rem(20);
	}

	&__price-head {
		color: #777;
		text-align: center;
		font-size: rem(12);
		font-style: normal;
		font-weight: 400;
		line-height: rem(12);
	}

	&__price-text {
		color: var(--theme-color);
		text-align: center;
		font-size: rem(18);
		font-style: normal;
		font-weight: 700;
		line-height: rem(20);
		span {
			color: #141414;
			font-size: rem(12);
			font-style: normal;
			font-weight: 700;
			line-height: rem(20);
		}
	}
}

/* 调整水平和垂直滚动卡片样式 */
.detail {
	&__similar-horizontal-item,
	&__similar-vertical-item {
		overflow: hidden;
		cursor: pointer;
		transition: transform 0.3s ease;
		border-radius: rem(24);
		border: 1px solid #e9e9e9;
		background: linear-gradient(180deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.08) 50%, rgba(var(--theme-color-r), var(--theme-color-g), var(--theme-color-b), 0.08) 100%);
		padding: rem(20);

		&:hover {
			transform: translateY(-4px);
		}
	}

	&__similar-horizontal-list {
		display: flex;
		overflow-x: auto;
		gap: rem(20);
		scrollbar-width: none; /* Firefox */
		-ms-overflow-style: none; /* IE and Edge */
		padding-bottom: rem(10);

		&::-webkit-scrollbar {
			display: none; /* Chrome, Safari, Opera */
		}
	}

	&__similar-horizontal-item {
		min-width: rem(280);
		flex: 0 0 rem(280);
	}

	&__similar-vertical-list {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(rem(280), 1fr));
		gap: rem(20);
	}
}

// PC和移动端样式
.detect_deviece_pc,
.detect_deviece_desktop {
	.detail {
		&__similar-vertical-list {
			grid-template-columns: repeat(4, 1fr);
		}
	}
}

.detect_deviece_mobile {
	.detail {
		&__similar-vertical-list {
			grid-template-columns: 1fr;
		}
	}
}

// 响应式样式 - PC设备
.detect_deviece_pc,
.detect_deviece_desktop {
	.detail {
		--fontsize-base: #{$vw_fontsize_pc};
		font-size: calc(var(--fontsize-base) / #{$vw_design_pc} * 100vw);
	}
}

// 响应式样式 - 移动设备
.detect_deviece_mobile {
	.detail {
		--fontsize-base: #{$vw_fontsize_mobile};
		font-size: calc(var(--fontsize-base) / #{$vw_design_mobile} * 100vw);

		// 隐藏PC端的顶部布局
		&__top-section {
			display: none;
		}

		&__container {
			padding: 0;
		}

		// 移动端顶部样式
		&__mobile-top {
			position: relative;
			width: 100%;
		}

		&__mobile-background {
			position: absolute;
			width: 100%;
			height: 100%;
			left: 0;
			top: 0;
			background: #141414;
		}

		&__mobile-texture {
			position: absolute;
			width: 100%;
			height: 100%;
			left: 0;
			top: 0;
			background-image: url('~@/assets/detail-texture.png');
			background-size: cover;
			opacity: 0.2;
		}

		&__mobile-header {
			position: relative;
			z-index: 1;
			padding: rem(32) 0 rem(32) rem(16);
			display: flex;
			flex-direction: column;
			gap: rem(20);
		}

		&__mobile-brand {
			display: flex;
			flex-direction: row;
			gap: rem(16);
			align-items: center;
		}

		&__mobile-logo-wrapper {
			width: rem(58);
			height: rem(58);
			border-radius: 50%;
			background: #ffffff;
			border: 1px solid #e9e9e9;
			display: flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
		}

		&__mobile-logo {
			width: rem(48);
			height: rem(48);
			object-fit: contain;
			border-radius: 50%;
		}

		&__mobile-info {
			display: flex;
			flex-direction: column;
			gap: rem(12);
		}

		&__mobile-title {
			font-size: rem(32);
			line-height: rem(32);
			color: #ffffff;
			margin: 0;
			font-weight: 700;
		}

		&__mobile-brand-name {
			font-size: rem(14);
			line-height: rem(14);
			color: #ffffff;
			font-weight: 400;
		}

		&__mobile-cards {
			display: flex;
			flex-direction: row;
			gap: rem(8);
			overflow-x: auto;
			padding-bottom: rem(5);
			white-space: nowrap;
			scrollbar-width: none;
			-ms-overflow-style: none;

			&::-webkit-scrollbar {
				display: none;
			}
		}

		&__mobile-card {
			background: #ffffff;
			border-radius: rem(12);
			border: 1px solid #e9e9e9;
			padding: rem(16);
			display: flex;
			flex-direction: row;
			gap: rem(12);
			align-items: center;
			flex-shrink: 0;
		}

		&__mobile-card-icon {
			width: rem(32);
			height: rem(32);
			display: flex;
			align-items: center;
			justify-content: center;
			background: var(--theme-color);
			border-radius: rem(4);
			flex-shrink: 0;

			img {
				width: rem(16);
				height: rem(16);
			}
		}

		&__mobile-card-content {
			display: flex;
			flex-direction: column;
			gap: rem(6);
		}

		&__mobile-card-value {
			font-size: rem(14);
			line-height: rem(14);
			font-weight: 500;
			color: #141414;
			white-space: nowrap;
		}

		&__mobile-card-label {
			font-size: rem(12);
			line-height: rem(12);
			color: #777777;
			white-space: nowrap;
		}

		// 移动端图库
		&__mobile-gallery {
			display: flex;
			flex-direction: column;
			gap: rem(8);
			width: 100%;
		}

		&__mobile-gallery-main {
			width: 100%;
			height: rem(250);
		}

		&__mobile-main-img {
			width: 100%;
			height: 100%;
			object-fit: cover;
			border-radius: 0 0 rem(36) 0;
		}

		&__mobile-thumbnails {
			display: flex;
			flex-direction: row;
			gap: rem(8);
			overflow-x: auto;
			padding: 0 rem(8);
			scrollbar-width: none;
			-ms-overflow-style: none;

			&::-webkit-scrollbar {
				display: none;
			}
		}

		&__mobile-thumbnail {
			width: rem(100);
			height: rem(66);
			flex-shrink: 0;
			border-radius: rem(12);
			border: 1px solid #e9e9e9;
			overflow: hidden;

			&--active {
				border: 3px solid var(--theme-color);
				padding: 2px;
				.detail__mobile-thumbnail-img {
					border: 2px solid #fff;
					border-radius: rem(12);
				}
			}
		}

		&__mobile-thumbnail-img {
			width: 100%;
			height: 100%;
			object-fit: cover;
		}

		// 内容区域调整
		&__content {
			padding: rem(40) 0;
		}

		//
		&__section {
			border-bottom: none;
			padding-bottom: 0;
			margin-bottom: rem(40);
			padding: 0 rem(16);
		}
		&__section-header {
			margin-bottom: rem(20);
		}
		&__section-content {
			color: #777;
			font-size: rem(13);
			font-style: normal;
			font-weight: 400;
			line-height: rem(20); /* 153.846% */
		}
	}
}
</style>
